import { motion } from "framer-motion";
import { useState } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Images from "../../utils/Images";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setIsSubscribed(true);
      setEmail("");
      // Reset success message after 3 seconds
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const footerLinks = {
    solutions: [],
    company: [
      { name: "About Us", href: "/about" },
      { name: "Terms of Service", href: "/terms-of-service" },
      // { name: "Privacy Policy", href: "/privacy" },
    ],
    support: [
      { name: "Help Center", href: "/help" },
      { name: "Contact Us", href: "/support" },
    ],
  };

  const socialLinks = [
    {
      name: "Facebook",
      icon: FaFacebook,
      href: "https://www.facebook.com/tripsecuregh",
      color: "hover:text-blue-600",
    },
    {
      name: "Twitter",
      icon: FaTwitter,
      href: "https://www.twitter.com/tripsecuregh",
      color: "hover:text-blue-400",
    },
    {
      name: "LinkedIn",
      icon: FaLinkedin,
      href: "https://www.linkedin.com/company/tripsecure",
      color: "hover:text-blue-700",
    },
    {
      name: "Instagram",
      icon: FaInstagram,
      href: "https://www.instagram.com/tripsecuregh",
      color: "hover:text-pink-500",
    },
    {
      name: "YouTube",
      icon: FaYoutube,
      href: "https://www.youtube.com/@tripsecure",
      color: "hover:text-red-600",
    },
  ];

  return (
    <footer className="text-white bg-gray-900">
      {/* Main Footer Content */}
      <div className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-6"
              >
                <div className="flex items-center mb-4 space-x-2">
                  <img
                    src={Images.logo_white}
                    alt="Tripsecure"
                    className="w-48"
                  />
                </div>
                <p className="mb-6 leading-relaxed text-gray-400">
                  Empowering insurance providers with cutting-edge technology to
                  deliver exceptional customer experiences and drive business
                  growth.
                </p>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-gray-400">
                    <FaPhone size={16} />
                    <span>+233 20 401 4370</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-400">
                    <FaEnvelope size={16} />
                    <span>info@tripsecuregh.com</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-400">
                    <FaMapMarkerAlt size={16} />
                    <span>Dokua Plaza, Accra, Ghana</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Company Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="mb-4 text-lg font-semibold">Company</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 transition-colors duration-200 hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Support Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="mb-4 text-lg font-semibold">Support</h3>
              <ul className="space-y-2">
                {footerLinks.support.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 transition-colors duration-200 hover:text-white"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Newsletter Signup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="pt-8 mt-12 border-t border-gray-800"
          >
            <div className="text-center">
              <h3 className="mb-4 text-xl font-semibold">Stay Updated</h3>
              <p className="mx-auto mb-6 max-w-2xl text-gray-400">
                Subscribe to our newsletter for the latest platform updates,
                industry insights, and exclusive content.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-4 mx-auto max-w-md sm:flex-row">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 placeholder-gray-400 text-white bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <button 
                  type="submit"
                  className="px-6 py-3 font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg transition-all duration-300 hover:from-blue-700 hover:to-indigo-800"
                >
                  Subscribe
                </button>
              </form>
              {isSubscribed && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 text-sm text-green-400"
                >
                  ✓ Successfully subscribed to our newsletter!
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="px-4 py-8 border-t border-gray-800">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col justify-between items-center space-y-4 md:flex-row md:space-y-0">
            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-sm text-gray-400"
            >
              © 2024 TripSecure. All rights reserved.
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex space-x-6"
            >
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-400 transition-colors duration-200 ${social.color}`}
                  aria-label={social.name}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </motion.div>

            {/* Legal Links */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex space-x-6 text-sm text-gray-400"
            >
              <Link
                to="/privacy"
                className="transition-colors duration-200 hover:text-white"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="transition-colors duration-200 hover:text-white"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookie-policy"
                className="transition-colors duration-200 hover:text-white"
              >
                Cookie Policy
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
}
